import type {OfferedAddon} from '../api/addons/types'
import type {Equipment} from '../api/booking/types'
import {addonPriceForQuantity, roundMoney} from './addon-price'

// Add-on lines ride in `participants[i].equipments[]`, next to the old name-based equipment lines.
// In a request the server wants only `{addonId, quantity}` and prices the line itself; in the booking
// form the line also carries what the screens need to show it (name, an estimated total).

export type AddonRequestLine = {addonId: string; quantity: number}

type WithEquipments = {equipments?: Equipment[]}

export const isAddonLine = <L extends {addonId?: unknown}>(line?: L | null): line is L & {addonId: string} =>
  Boolean(line) && typeof line?.addonId === 'string' && line.addonId !== ''

// Old equipment lines pass through untouched; add-on lines shrink to what the server reads.
export const toRequestEquipments = (equipments?: Equipment[]): Equipment[] | undefined =>
  equipments?.map((line) => {
    if (!isAddonLine(line)) {
      return line
    }
    const requestLine: AddonRequestLine = {addonId: line.addonId, quantity: line.quantity}
    // The request carries no name or price for an add-on; the type of the form's lines is wider than that.
    return requestLine as unknown as Equipment
  })

export const shapeParticipantsForRequest = <P extends WithEquipments>(participants: P[]): P[] =>
  participants.map((participant) =>
    participant.equipments?.some(isAddonLine)
      ? {...participant, equipments: toRequestEquipments(participant.equipments)}
      : participant,
  )

export const shapeQuoteOptions = <P extends WithEquipments, O extends {participants?: P[]}>(options: O[]): O[] =>
  options.map((option) =>
    option.participants ? {...option, participants: shapeParticipantsForRequest(option.participants)} : option,
  )

// A PER_BOOKING add-on is one choice for the whole booking: it sits on the first participant only.
export const isAddonForParticipant = (addon: Pick<OfferedAddon, 'scope'>, participantIndex: number): boolean =>
  addon.scope !== 'PER_BOOKING' || participantIndex === 0

const DEFAULT_MAX = 20

// The most this participant may take. A PER_BOOKING add-on uses its booking cap when it has one. A PER_PERSON add-on
// with a cap across the whole booking is limited by what the other participants already hold.
export const addonMaxQuantity = (
  addon: Pick<OfferedAddon, 'maxPerBooking' | 'maxPerPerson' | 'scope'>,
  bookedElsewhere = 0,
): number => {
  const perPerson = Number(addon.maxPerPerson) > 0 ? Number(addon.maxPerPerson) : DEFAULT_MAX
  const perBooking = Number(addon.maxPerBooking) > 0 ? Number(addon.maxPerBooking) : null
  if (addon.scope === 'PER_BOOKING') {
    return perBooking ?? perPerson
  }
  return perBooking === null ? perPerson : Math.max(0, Math.min(perPerson, perBooking - bookedElsewhere))
}

// A line for the booking form. `lineTotal` here is an estimate for display; the quote replaces it.
export const buildAddonLine = (addon: OfferedAddon, quantity: number): Equipment => {
  const estimate = addon.included ? 0 : addonPriceForQuantity(addon, quantity) ?? 0
  return {
    addonId: addon.addonId,
    name: addon.name,
    value: addon.name,
    label: addon.name,
    kind: addon.kind,
    quantity,
    price: quantity > 0 ? roundMoney(estimate / quantity) : 0,
    lineTotal: estimate,
    included: addon.included,
    scope: addon.scope,
  }
}

// How many of each add-on the participants other than `participantIndex` already hold.
export const addonQuantitiesElsewhere = (
  participants: WithEquipments[],
  participantIndex: number,
): Record<string, number> => {
  const totals: Record<string, number> = {}
  participants.forEach((participant, index) => {
    if (index === participantIndex) {
      return
    }
    participant.equipments?.forEach((line) => {
      if (line.addonId) {
        totals[line.addonId] = (totals[line.addonId] || 0) + (line.quantity || 0)
      }
    })
  })
  return totals
}

// Included add-ons are bundled with the service: every participant (the first one only, for a PER_BOOKING add-on)
// gets one, whether or not the customer opens the Add-ons pop-up. Lines already there are kept as they are.
export const withIncludedAddonLines = <P extends WithEquipments>(participants: P[], offered: OfferedAddon[]): P[] => {
  const included = offered.filter((addon) => addon.included)
  if (!included.length) {
    return participants
  }
  return participants.map((participant, index) => {
    const lines = participant.equipments || []
    const missing = included.filter(
      (addon) => isAddonForParticipant(addon, index) && !lines.some((line) => line.addonId === addon.addonId),
    )
    if (!missing.length) {
      return participant
    }
    return {...participant, equipments: [...lines, ...missing.map((addon) => buildAddonLine(addon, 1))]}
  })
}

// "2026-11-02" from the booking form's "02/11/2026"; undefined when it is not a date.
export const toIsoDate = (value?: string | null): string | undefined => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || '')
  return match ? `${match[3]}-${match[2]}-${match[1]}` : undefined
}
